import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  CloudDownload as ScrapeIcon,
  PlaylistAddCheck as MatchIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { sgpbusinessAPI } from '../../services/api';
import SGPBusinessReviewDialog from './SGPBusinessReviewDialog';

const SOURCE_LABELS = {
  gebiz: 'GeBIZ',
  sgpbusiness_alert: 'SGP Alert',
  unspecified: 'Unspecified',
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : 'Never');

/**
 * Job Overview card for the SGPBusiness alert list: lead source breakdown,
 * scrape/match status, and the Scrape Now / Run Matching actions.
 */
const SGPBusinessOverviewCard = ({ jobId, jobStats }) => {
  const queryClient = useQueryClient();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  const notify = (message, severity = 'info') => setSnackbar({ open: true, message, severity });

  const { data: status, isLoading } = useQuery(
    ['sgpbusinessStatus', jobId],
    () => sgpbusinessAPI.getStatus(jobId),
    { enabled: !!jobId }
  );

  // Scrape only changes the SGPBusiness companies, so it only refreshes status.
  const invalidateStatus = () => queryClient.invalidateQueries(['sgpbusinessStatus', jobId]);

  // Matching flags/creates leads, so it also refreshes job stats, the leads
  // list, and its count badge (a separate query in JobDetail).
  const invalidateAfterMatch = () => {
    invalidateStatus();
    queryClient.invalidateQueries(['sgpbusinessReview', jobId]); // queue changed
    queryClient.invalidateQueries(['jobStats', jobId]);
    queryClient.invalidateQueries(['jobLeads', jobId]);
    queryClient.invalidateQueries(['jobLeadsCount', jobId]);
  };

  const scrapeMutation = useMutation(() => sgpbusinessAPI.scrape(jobId), {
    onSuccess: (data) => {
      notify(data.message || 'Scrape complete', 'success');
      invalidateStatus();
    },
    onError: (error) =>
      notify(`Scrape failed: ${error.response?.data?.detail || error.message}`, 'error'),
  });

  const matchMutation = useMutation(() => sgpbusinessAPI.match(jobId, false), {
    onSuccess: (data) => {
      const needsReview = (data.fuzzy_review || 0) > 0;
      const extra = needsReview ? ` — ${data.fuzzy_review} need review` : '';
      notify(`${data.message}${extra}`, needsReview ? 'warning' : 'success');
      invalidateAfterMatch();
      if (needsReview) setReviewOpen(true);
    },
    onError: (error) =>
      notify(`Matching failed: ${error.response?.data?.detail || error.message}`, 'error'),
  });

  const confirmMatching = () => {
    setConfirmOpen(false);
    matchMutation.mutate();
  };

  const leadsBySource = jobStats?.leads_by_source || {};
  const onAlertCount = jobStats?.on_alert_list_count ?? 0;
  const pendingReview = status?.pending_review_count ?? 0;

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        avatar={<WarningIcon color="warning" />}
        title="SGPBusiness Alert List"
        titleTypographyProps={{ variant: 'h6' }}
      />
      <CardContent sx={{ pt: 0 }}>
        {/* Lead source breakdown */}
        <Grid container spacing={2} sx={{ mb: 1 }}>
          {Object.entries(leadsBySource).map(([src, count]) => (
            <Grid item xs={6} sm={3} key={src}>
              <Typography variant="caption" color="text.secondary" display="block">
                {SOURCE_LABELS[src] || src}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{count}</Typography>
            </Grid>
          ))}
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary" display="block">
              On Alert List
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#ed6c02' }}>
              {onAlertCount}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Scrape / match status */}
        {isLoading ? (
          <Box sx={{ py: 1 }}><CircularProgress size={20} /></Box>
        ) : (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">Last Scraped</Typography>
              <Typography variant="body2">{formatDate(status?.last_scraped_at)}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">Tracked Companies</Typography>
              <Typography variant="body2">{status?.total_tracked_companies ?? 0}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">New Since Last</Typography>
              <Typography variant="body2">{status?.new_since_last_scrape ?? 0}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">Pending Review</Typography>
              <Chip
                size="small"
                label={pendingReview}
                color={pendingReview > 0 ? 'warning' : 'default'}
              />
            </Grid>
          </Grid>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={scrapeMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <ScrapeIcon />}
            onClick={() => scrapeMutation.mutate()}
            disabled={scrapeMutation.isLoading}
          >
            Scrape Now
          </Button>
          <Button
            variant="outlined"
            startIcon={matchMutation.isLoading ? <CircularProgress size={16} /> : <MatchIcon />}
            onClick={() => setConfirmOpen(true)}
            disabled={matchMutation.isLoading}
          >
            Run Matching
          </Button>
          {pendingReview > 0 && (
            <Button variant="outlined" color="warning" onClick={() => setReviewOpen(true)}>
              Review ({pendingReview})
            </Button>
          )}
        </Box>
      </CardContent>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Run matching?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This flags exact matches on existing leads, queues fuzzy matches for
            admin review, and creates new leads (with pending enrichment) for
            companies that don't match. New leads cannot be un-created in bulk.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmMatching} variant="contained" autoFocus>
            Run Matching
          </Button>
        </DialogActions>
      </Dialog>

      <SGPBusinessReviewDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        jobId={jobId}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default SGPBusinessOverviewCard;
